'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

export default function LoginForm({ onSwitchToSignup }: { onSwitchToSignup: () => void }) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { signIn } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setAuthError(null);
    try {
      await signIn(data.email, data.password);
      const redirectTo = searchParams.get('redirectTo') || '/dashboard';
      router.push(redirectTo);
      router.refresh();
    } catch (error: any) {
      setAuthError(error?.message || 'Invalid email or password. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <h2 className="text-2xl font-800 text-foreground">Welcome back</h2>
        <p className="text-sm text-muted-foreground mt-1">Sign in to manage your website and store.</p>
      </div>

      {/* Google auth */}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-3 py-3 bg-card border border-border rounded-xl text-sm font-600 text-foreground hover:bg-muted active:scale-95 transition-all duration-150 shadow-sm"
      >
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3">
        <hr className="flex-1 border-border" />
        <span className="text-xs text-muted-foreground">or sign in with email</span>
        <hr className="flex-1 border-border" />
      </div>

      {/* Auth error */}
      {authError && (
        <div className="bg-danger/10 border border-danger/20 rounded-xl px-4 py-3">
          <p className="text-sm text-danger">{authError}</p>
        </div>
      )}

      {/* Email */}
      <div>
        <label className="text-sm font-600 text-foreground block mb-1.5">Email address</label>
        <input
          type="email"
          placeholder="you@yourbusiness.com"
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
          className={`w-full px-4 py-3 text-sm bg-muted rounded-xl border focus:outline-none focus:bg-card transition-all ${
            errors.email ? 'border-danger focus:border-danger' : 'border-transparent focus:border-primary'
          }`}
        />
        {errors.email && <p className="text-xs text-danger mt-1.5">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-600 text-foreground">Password</label>
          <a href="/forgot-password" className="text-xs font-600 text-primary hover:underline">Forgot password?</a>
        </div>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Your password"
            {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'At least 6 characters' } })}
            className={`w-full px-4 py-3 pr-11 text-sm bg-muted rounded-xl border focus:outline-none focus:bg-card transition-all ${
              errors.password ? 'border-danger focus:border-danger' : 'border-transparent focus:border-primary'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-danger mt-1.5">{errors.password.message}</p>}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <input type="checkbox" id="rememberMe" {...register('rememberMe')} className="rounded accent-primary w-4 h-4" />
        <label htmlFor="rememberMe" className="text-sm text-muted-foreground">Remember me for 30 days</label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-700 hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-70"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
        {loading ? 'Signing in…' : 'Sign In'}
      </button>

      <p className="text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <button type="button" onClick={onSwitchToSignup} className="text-primary font-600 hover:underline">
          Create one free
        </button>
      </p>
    </form>
  );
}